import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { finalize } from 'rxjs/operators';

import { AuthQuery } from '@app/auth/auth.query';
import { ImportSyncService, SourceRecord, SourceRecordVersion } from '@app/services/import-sync.service';
import { IMPORT_PROFILES, normalizeHeader } from '@app/system-settings/import-profiles';

interface SourceColumnView {
  column: string;
  value: string;
  /** Target field when the column is imported, otherwise null */
  target: string | null;
  changed: boolean;
  previous?: string;
}

const SOURCE_TYPE_LABELS: { [type: string]: string } = {
  'kunden-master': 'Kunden-Stammdaten (Pers.-Nr.)',
  clients: 'Kundenexport / Vertrag (Erf.-Nr.)',
  artikel: 'Artikelexport (Art.-Nr.)',
};

/**
 * "Original BMD-Datensatz": the unmodified export rows behind an entity.
 * Superuser only (role 1): the panel renders nothing for other roles and the
 * API answers 403 - the API check is what protects the data.
 */
@Component({
  selector: 'app-source-record-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule],
  templateUrl: './source-record-panel.component.html',
})
export class SourceRecordPanelComponent implements OnChanges {
  @Input() entityType!: 'kunde' | 'standort' | 'artikel';
  @Input() entityId: string | number | null | undefined;

  expanded = false;
  loading = false;
  loaded = false;
  error = '';
  records: SourceRecord[] = [];
  /** Selected version index per record (0 = newest) */
  selectedVersion: { [key: string]: number } = {};
  showEmpty = false;

  constructor(private authQuery: AuthQuery, private importSyncService: ImportSyncService) {}

  get isSuperUser(): boolean {
    const role = this.authQuery.role as any;
    return (typeof role === 'string' ? parseInt(role, 10) : role) === 1;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['entityId'] || changes['entityType']) && this.loaded) {
      this.loaded = false;
      this.records = [];
      if (this.expanded) {
        this.load();
      }
    }
  }

  toggle(): void {
    this.expanded = !this.expanded;
    if (this.expanded && !this.loaded) {
      this.load();
    }
  }

  load(): void {
    if (!this.isSuperUser || this.entityId === null || this.entityId === undefined || this.entityId === '') {
      return;
    }
    this.loading = true;
    this.error = '';
    this.importSyncService
      .sourceRecords(this.entityType, this.entityId)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe(
        (records) => {
          this.records = records;
          this.loaded = true;
          records.forEach((r) => (this.selectedVersion[this.recordKey(r)] = 0));
        },
        (err) => (this.error = err?.message || 'Originaldaten konnten nicht geladen werden')
      );
  }

  recordKey(r: SourceRecord): string {
    return r.source_type + '|' + r.source_key;
  }

  sourceTypeLabel(type: string): string {
    return SOURCE_TYPE_LABELS[type] || type;
  }

  version(r: SourceRecord): SourceRecordVersion {
    return r.versions[this.selectedVersion[this.recordKey(r)] ?? 0];
  }

  columns(r: SourceRecord): SourceColumnView[] {
    const index = this.selectedVersion[this.recordKey(r)] ?? 0;
    const current = r.versions[index];
    const previous = r.versions[index + 1];
    const profile = IMPORT_PROFILES.find((p) => p.id === r.source_type);
    const targets = new Map<string, string>();
    profile?.columns.forEach((c) => [c.name, ...(c.aliases || [])].forEach((h) => targets.set(normalizeHeader(h), c.target)));

    return Object.keys(current.raw)
      .filter((column) => column.trim() !== '')
      .map((column) => {
        const value = String(current.raw[column] ?? '');
        const before = previous ? String(previous.raw[column] ?? '') : undefined;
        return {
          column,
          value,
          target: targets.get(normalizeHeader(column)) ?? null,
          changed: previous !== undefined && before !== value,
          previous: before,
        };
      })
      .filter((c) => this.showEmpty || c.value.trim() !== '' || c.changed);
  }

  emptyCount(r: SourceRecord): number {
    return Object.values(this.version(r).raw).filter((v) => String(v ?? '').trim() === '').length;
  }

  changedCount(r: SourceRecord): number {
    const index = this.selectedVersion[this.recordKey(r)] ?? 0;
    const previous = r.versions[index + 1];
    if (!previous) {
      return 0;
    }
    const current = r.versions[index].raw;
    const keys = new Set([...Object.keys(current), ...Object.keys(previous.raw)]);
    return Array.from(keys).filter((k) => String(current[k] ?? '') !== String(previous.raw[k] ?? '')).length;
  }
}
